export const WEIGHT_TOLERANCE = 0.001;

export function sumWeights(items) {
  return (items || []).reduce((acc, item) => acc + (Number(item && item.weight) || 0), 0);
}

export function sumsToOne(sum, eps = WEIGHT_TOLERANCE) {
  return Math.abs(Number(sum) - 1) <= eps;
}

/**
 * Weights are STORED as fractions of one whole (0.2) and SHOWN and TYPED as
 * percentages (20%). These two functions are the only place that crosses the
 * line, so a screen never multiplies or divides by 100 on its own and the
 * rounding is decided once.
 *
 * Two decimals of percentage (a hundredth of a percent) is the resolution.
 * That is finer than anyone weighs a requirement, and it is what makes the
 * round trip exact: every percentage with at most two decimals survives
 * `percentToWeight` then `weightToPercent` unchanged, because the fraction is
 * rounded to four decimals rather than left as whatever floating point makes of
 * 12.5 / 100. Without that, a value typed and read back could drift by a digit
 * and two inputs that were typed to total 100 could total 99.99999.
 *
 * Nothing, rubbish and negatives are 0 — a field being cleared is not a reason
 * to store NaN.
 */
export function weightToPercent(weight) {
  const n = Number(weight);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 10000) / 100;
}

export function percentToWeight(percent) {
  const n = Number(percent);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100) / 10000;
}

function fmt(n) {
  return (Number(n) || 0).toFixed(2);
}

function fmtGap(gap) {
  const n = Number(gap) || 0;
  if (Math.abs(n) <= WEIGHT_TOLERANCE) return '0.00';
  const sign = n > 0 ? '+' : '−';
  return `${sign}${Math.abs(n).toFixed(2)}`;
}

export function describeWeightSums(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const parts = [];
  let ok = true;
  for (const g of list) {
    const sum = sumWeights(g && g.criteria);
    const gap = sum - 1;
    if (!sumsToOne(sum)) {
      ok = false;
      const name = (g && g.name) || 'Group';
      parts.push(`${name}: criteria sum to ${fmt(sum)} (gap ${fmtGap(gap)}). Adjust so the group totals 1.00.`);
    }
  }
  const groupSum = sumWeights(list);
  if (!sumsToOne(groupSum)) {
    ok = false;
    parts.push(`Group weights sum to ${fmt(groupSum)} (gap ${fmtGap(groupSum - 1)}). Adjust so groups total 1.00.`);
  }
  if (ok) {
    const named = list.map((g) => g && g.name).filter(Boolean);
    const lead = named.length === 1
      ? `${named[0]}: criteria sum to 1.00.`
      : 'Each group’s criteria sum to 1.00.';
    return { ok: true, text: `${lead} Group weights sum to 1.00.` };
  }
  return { ok: false, text: parts.join(' ') };
}

/**
 * Columns that carry more than one whole.
 *
 * Weights describe shares of something, so a column may add up to less than
 * 100% while it is being written — but never to more. Over 100% is arithmetic
 * that cannot be honoured, and the evaluation must not move on with it.
 *
 * The allowance is 0.5%, so 0.33 + 0.33 + 0.34 passes.
 */
export const OVERWEIGHT_ALLOWANCE = 0.005;

export function overweightColumns(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const over = [];
  const sections = sumWeights(list);
  if (sections > 1 + OVERWEIGHT_ALLOWANCE) {
    over.push({ scope: 'sections', name: null, sum: sections });
  }
  for (const group of list) {
    const sum = sumWeights(group && group.criteria);
    if (sum > 1 + OVERWEIGHT_ALLOWANCE) {
      over.push({ scope: 'criteria', name: (group && group.name) || 'Section', sum });
    }
  }
  return over;
}

/** One sentence naming every column that is over, or '' when none is. */
export function describeOverweight(groups) {
  const over = overweightColumns(groups);
  if (!over.length) return '';
  const parts = over.map((row) => (row.scope === 'sections'
    ? `the sections add up to ${Math.round(row.sum * 100)}%`
    : `“${row.name}” adds up to ${Math.round(row.sum * 100)}%`));
  const list = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `Weights cannot add up to more than 100%: ${list}. Balance them on the Request step.`;
}

/**
 * Columns that do not add up to one whole — short of it OR over it.
 *
 * `overweightColumns` above is the old, one-sided rule: a column may be short
 * while it is being written, and the API only refuses what is over. The Request
 * step is stricter, because its weights now balance themselves as they are typed
 * (see `rebalanceAround`): by the time the owner asks to move on, every column
 * is meant to describe a whole, so a column that is short is a column nobody
 * finished. This is the rule `web/src/app/stages.ts` gates the step on.
 *
 * Same half-percent allowance as `overweightColumns`, on both sides, so
 * 0.33 + 0.33 + 0.34 passes. A section with no criteria is not a column, and an
 * evaluation with no sections has none either — "nothing yet" is the URS
 * stage's business ("add at least one requirement"), not this one's.
 */
export function unbalancedColumns(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const off = [];
  if (list.length && Math.abs(sumWeights(list) - 1) > OVERWEIGHT_ALLOWANCE) {
    off.push({ scope: 'sections', name: null, id: null, sum: sumWeights(list) });
  }
  for (const group of list) {
    const criteria = (group && group.criteria) || [];
    if (!criteria.length) continue;
    const sum = sumWeights(criteria);
    if (Math.abs(sum - 1) > OVERWEIGHT_ALLOWANCE) {
      off.push({ scope: 'criteria', name: (group && group.name) || 'Section', id: (group && group.id) || null, sum });
    }
  }
  return off;
}

/**
 * One weight in a column has just been typed; make the column total 100% again.
 *
 * `values` are the column as stored (fractions), `editedIndex` the row being
 * typed in and `typedPercent` exactly what was typed (a number or the raw
 * string out of the input). Returns the whole column as fractions:
 *
 *  - the typed row keeps what was typed, to a hundredth of a percent — it is
 *    the one row that must never be moved by the arithmetic, or the box under
 *    the owner's fingers would argue with them;
 *  - the OTHER rows share the remaining (100 − typed)% in proportion to what
 *    they weighed, so a 50/30/20 column that gets its first row typed to 70
 *    becomes 70/18/12, not 70/15/15;
 *  - rows that weigh nothing between them have no proportion to keep, so they
 *    split the remainder equally;
 *  - typed 100 or more is clamped to 100 and everything else goes to 0;
 *  - a column of one row can only be 100%;
 *  - nothing, rubbish and negatives are 0, as `percentToWeight` reads them.
 *
 * The arithmetic is done in whole hundredths of a percent — the resolution
 * `weightToPercent` / `percentToWeight` already fix — so there is no floating
 * point to drift. Rounding the others will not usually land on the remainder
 * exactly; the difference goes onto the LARGEST of the other rows (where a few
 * hundredths of a percent are least visible), never onto the typed one. If that
 * difference is negative and larger than the largest row can give, it carries on
 * to the next largest rather than leaving a negative weight behind.
 */
export function rebalanceAround(values, editedIndex, typedPercent) {
  const column = Array.isArray(values) ? values : [];
  const n = column.length;
  if (!n) return [];
  if (n === 1) return [1];
  if (!Number.isInteger(editedIndex) || editedIndex < 0 || editedIndex >= n) {
    return column.map((value) => weightOf(value));
  }

  const WHOLE = 10000; // 100.00% in hundredths of a percent
  const typed = Math.min(WHOLE, Math.round(weightToPercent(percentToWeight(typedPercent)) * 100));
  const remaining = WHOLE - typed;

  const others = [];
  for (let i = 0; i < n; i += 1) if (i !== editedIndex) others.push(i);

  const shares = distribute(others.map((i) => column[i]), remaining);

  const result = new Array(n);
  result[editedIndex] = typed / WHOLE;
  others.forEach((i, k) => {
    result[i] = shares[k] / WHOLE;
  });
  return result;
}

/**
 * Share `remaining` hundredths of a percent between rows in proportion to their
 * weights, to whole hundredths, drift on the largest row.
 *
 * Weights are taken in whole hundredths too, so two rows that were equal are
 * treated as equal: with floats 0.3 and 0.3 can round to different hundredths
 * once scaled, and the drift rule would then be arguing with floating point
 * noise. Rows that weigh nothing between them have no proportion to keep, so
 * they split equally. A negative surplus is carried from the largest row to the
 * next largest rather than leaving a negative weight behind.
 */
function distribute(values, remaining) {
  const weights = values.map((value) => Math.round(weightOf(value) * 10000));
  const total = weights.reduce((sum, w) => sum + w, 0);
  const shares = weights.map((w) => (total > 0 ? (remaining * w) / total : remaining / weights.length));
  const hundredths = shares.map((share) => Math.round(share));

  let drift = remaining - hundredths.reduce((sum, h) => sum + h, 0);
  if (drift !== 0) {
    // Largest first, ties to the earlier row so the answer is deterministic.
    const bySize = hundredths.map((_, k) => k).sort((x, y) => hundredths[y] - hundredths[x] || x - y);
    if (drift > 0) {
      hundredths[bySize[0]] += drift;
    } else {
      for (const k of bySize) {
        const take = Math.min(hundredths[k], -drift);
        hundredths[k] -= take;
        drift += take;
        if (drift === 0) break;
      }
    }
  }
  return hundredths;
}

/**
 * A whole column scaled to exactly 100%, keeping its proportions — what a column
 * needs after a row is REMOVED, which leaves it short. Same arithmetic as
 * `rebalanceAround`; with nothing to go on, an equal split; one row, 100%.
 */
export function normaliseWeights(values) {
  const column = Array.isArray(values) ? values : [];
  if (!column.length) return [];
  return distribute(column, 10000).map((h) => h / 10000);
}

/**
 * The column after a row is ADDED: the new row takes an equal share (100 / n
 * of the n rows now in it, to a hundredth) and the rows already there give up
 * room in proportion, so what the owner had set keeps its proportions.
 * Returns the whole column, the new row last.
 */
export function rebalanceWithNewRow(values) {
  const existing = Array.isArray(values) ? values : [];
  const n = existing.length + 1;
  return rebalanceAround([...existing, 0], n - 1, Math.round(10000 / n) / 100);
}

function weightOf(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * The same weights, as shares of one whole.
 *
 * A column may be written as relative importance — 5, 3, 3, 1 — or as shares
 * already. Either way the evaluation needs fractions that add up to 1.00, so
 * the column is scaled, rounded to two decimals, and the rounding difference
 * is put on the heaviest weight: 0.33 + 0.33 + 0.34, never 0.99.
 *
 * With nothing to go on — no weights, or all zero — an equal split is the only
 * reading that does not invent a preference.
 */
export function balanceShares(values) {
  const numbers = (values || []).map((value) => Math.max(0, Number(value) || 0));
  if (!numbers.length) return [];
  const total = numbers.reduce((sum, value) => sum + value, 0);
  const even = 1 / numbers.length;
  const shares = total > 0 ? numbers.map((value) => value / total) : numbers.map(() => even);
  const rounded = shares.map((share) => Math.round(share * 100) / 100);
  const drift = Math.round((1 - rounded.reduce((sum, value) => sum + value, 0)) * 100) / 100;
  if (drift !== 0) {
    let heaviest = 0;
    for (let i = 1; i < rounded.length; i += 1) if (rounded[i] > rounded[heaviest]) heaviest = i;
    rounded[heaviest] = Math.round((rounded[heaviest] + drift) * 100) / 100;
  }
  return rounded;
}

/** Groups whose weights, and whose criteria weights, are shares of one whole. */
export function balanceGroupShares(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const groupShares = balanceShares(list.map((group) => group && group.weight));
  return list.map((group, i) => {
    const criteria = (group && group.criteria) || [];
    const criteriaShares = balanceShares(criteria.map((c) => c && c.weight));
    return {
      ...group,
      weight: groupShares[i],
      criteria: criteria.map((criterion, j) => ({ ...criterion, weight: criteriaShares[j] })),
    };
  });
}
